import type { LDContext } from 'launchdarkly-js-client-sdk';
import type { UserData } from '@ptml/root-config';

/**
 * Additional LaunchDarkly targeting attributes sourced from claims that aren't part of the
 * standard `UserData` shape (e.g. decoded server-side from the raw JWT by the BFF). All
 * fields are optional since presence of the underlying claims isn't guaranteed.
 */
export type LaunchDarklyContextEnrichment = {
    companyUniqueId?: string;
    companyType?: string;
    drpMembership?: string;
};

const ANONYMOUS_CONTEXT: LDContext = {
    kind: 'user',
    key: 'user-not-logged-in',
    anonymous: true,
};

/**
 * Builds the LaunchDarkly targeting context for the current user.
 *
 * @param user The authenticated user's data, or `null`/`undefined` if not logged in.
 * @param enrichment Optional additional attributes sourced from BFF-decoded JWT claims.
 * @returns The LaunchDarkly context to initialise/identify the SDK with.
 */
export const buildLaunchDarklyContext = (
    user?: UserData | null,
    enrichment?: LaunchDarklyContextEnrichment,
): LDContext => {
    if (!user?.company_displayid) {
        return ANONYMOUS_CONTEXT;
    }

    const context: LDContext = {
        kind: 'user',
        key: `c-${user.company_displayid}`,
        name: user.company_name || undefined,
        companyId: user.companyid || undefined,
        companyDisplayId: user.company_displayid,
        companyTypeId: user.company_typeid || undefined,
    };

    if (enrichment?.companyUniqueId) {
        context.companyUniqueId = enrichment.companyUniqueId;
    }

    if (enrichment?.companyType) {
        context.companyType = enrichment.companyType;
    }

    if (enrichment?.drpMembership) {
        context.drpMembership = enrichment.drpMembership;
    }

    return context;
};
